import React from 'react';
import { GuideLayout, GuideList, GuideSteps, GuideTip } from '../components/GuideLayout';

const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started & Admin Sign-In',
    body: (
      <>
        <GuideSteps steps={[
          'Open the Admin sign-in page (/admin-login) and log in with your administrator email and password.',
          'You land on the Admin Dashboard; the Console, Telemetry and AI Transparency pages are linked from the header.',
          'If your session expires you are redirected back to the admin sign-in automatically.',
        ]} />
      </>
    ),
  },
  {
    id: 'console-members',
    title: 'Admin Console — Managing Members',
    body: (
      <>
        <p>The Console is your day-to-day workspace for participating members.</p>
        <GuideList items={[
          'Add Member: enter the company name only — a unique Company Code and 6-character Invite Code are generated automatically. Email is optional.',
          'CSV Upload: bulk-add members with a "Company Name, Email" file.',
          'Copy credentials: per-row copy buttons for the Company Code and Invite Code, a copy-ready invite message per member, and a "Copy all invites" bulk button.',
          'Regenerate Code: issues a fresh Invite Code (the old one stops working immediately).',
          'Activate / Deactivate: temporarily block or restore a member\u2019s access without deleting data.',
          'Delete: permanently removes the member and their response data.',
          'Expand a row (click it) to see invite date, first login, last edit, compliance acceptance timestamp and the response code.',
        ]} />
      </>
    ),
  },
  {
    id: 'test-members',
    title: 'Test Members & Data Hygiene',
    body: (
      <>
        <GuideList items={[
          'Mark as test (flask icon): flags a member as test/demo. A purple TEST badge appears and the member is excluded from ALL statistics — completion rate, dashboard analytics, materiality matrix, KPI averages, momentum chart, the public teaser page and members\u2019 benchmark averages.',
          '"Hide test members" checkbox above the table hides flagged rows for a clean live-campaign view.',
          'Click the flask again to unflag; statistics recalculate instantly.',
          'Exports keep test members visible with a "Test" column so raw data stays complete.',
        ]} />
        <GuideTip>Flag internal trial accounts (e.g., "Test Corp") before sharing statistics — this keeps industry averages credible.</GuideTip>
      </>
    ),
  },
  {
    id: 'tracking',
    title: 'Participation Tracking, Reminders & Momentum',
    body: (
      <>
        <GuideList items={[
          'Stats cards summarise Total, Invited, Opened, In Progress, Completed and Edited members (test members excluded), with a completion-rate bar.',
          'Reminder tracker: members with no activity for your chosen number of days appear in an amber banner, split into "Invited, never opened" and "Tried, not completed" — each with a copy-ready nudge message.',
          'The Momentum chart on the Dashboard shows invited / opened / completed counts per week so you can watch campaign progress over time.',
        ]} />
      </>
    ),
  },
  {
    id: 'compliance',
    title: 'Compliance Acceptance Tracking',
    body: (
      <>
        <GuideList items={[
          'Respondents must accept the Member Acceptance Policy and Privacy Notice before taking the survey.',
          'The acceptance date & time appears in each member\u2019s expanded row ("Compliance Accepted") and in the Excel export.',
          'Members can download their own signed acceptance as part of their PDF report.',
        ]} />
      </>
    ),
  },
  {
    id: 'dashboard',
    title: 'Admin Dashboard — Analytics',
    body: (
      <>
        <GuideList items={[
          'Overview: summary KPIs, the Business Segment Distribution donut (click the "Support Services" slice to drill into its five sub-segments; use "← All segments" to return) and the Double Materiality Matrix with even 0–5 axes.',
          'KPI Analysis tab: industry averages and distributions for every quantitative KPI.',
          'Responses tab: inspect individual submissions and their change logs.',
          'All analytics exclude test-flagged members automatically.',
        ]} />
      </>
    ),
  },
  {
    id: 'exports',
    title: 'Exports (Excel & PDF)',
    body: (
      <>
        <GuideList items={[
          'Excel: full workbook — member roster (with Test column, codes, compliance timestamps) plus every response, ready for offline analysis.',
          'PDF Report: co-branded (Benchmark PH + IBPAP + SusTek) summary with participation stats and KPI averages; test members are excluded from the averages.',
          'Both are generated from the Console header buttons.',
        ]} />
      </>
    ),
  },
  {
    id: 'telemetry',
    title: 'Telemetry & AI Transparency',
    body: (
      <>
        <GuideList items={[
          'Telemetry page: API request volumes, latency and error rates, plus AI token usage and estimated cost per endpoint.',
          'AI Transparency page: documents where AI is used in the platform (Advisor, KPI narratives), which model powers it, and the safeguards applied.',
        ]} />
      </>
    ),
  },
  {
    id: 'public-teaser',
    title: 'Public "State of IT-BPM ESG" Page',
    body: (
      <>
        <GuideList items={[
          'A public teaser page (/state-of-esg) shares selected industry aggregates to encourage participation — no member-level data is exposed.',
          'It ships with social-share (OG) cards so links preview nicely on LinkedIn, Viber and other platforms.',
          'Test members are excluded from all public figures.',
        ]} />
      </>
    ),
  },
  {
    id: 'faq',
    title: 'Troubleshooting & FAQ',
    body: (
      <>
        <GuideList items={[
          'A member cannot sign in — confirm the exact Company + Invite Codes from their row; if compromised, use Regenerate Code and resend the invite message.',
          '"Your session has expired" — sign in again at /admin-login; this also happens if a member sign-in occurred in the same browser.',
          'Statistics look off — check for unflagged test members; flagged members are excluded everywhere automatically.',
          'A member asks to change answers after submitting — they can: edits are allowed and fully logged against the original submission.',
          'Production vs preview — changes ship to the live site only when a new build is deployed; check the build number in this guide\u2019s header.',
        ]} />
      </>
    ),
  },
];

export default function AdminGuidePage() {
  return (
    <GuideLayout
      title="Administrator User Guide"
      audience="For IBPAP programme administrators"
      subtitle="A complete reference for running the Benchmark PH campaign — member management, participation tracking, analytics, exports, telemetry and compliance."
      sections={sections}
      backTo="/admin/console"
      backLabel="Back to Console"
    />
  );
}
